import React from "react";

export function normalizeText(text: string): string {
  if (!text) return "";
  
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function findMatches(text: string, searchTerm: string): Array<{ start: number; end: number }> {
  if (!searchTerm || !text) return [];
  
  const normalizedText = normalizeText(text);
  const normalizedSearch = normalizeText(searchTerm);
  
  if (!normalizedSearch) return [];
  
  const matches: Array<{ start: number; end: number }> = [];
  
  let normalizedIndex = 0;
  while (true) {
    const matchIndex = normalizedText.indexOf(normalizedSearch, normalizedIndex);
    if (matchIndex === -1) break;
    let originalStart = -1;
    let originalEnd = -1;
    let currentNormalizedPos = 0;
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const normalizedChar = normalizeText(char);
      
      if (normalizedChar.length > 0) {
        if (currentNormalizedPos === matchIndex && originalStart === -1) {
          originalStart = i;
        }
        currentNormalizedPos += normalizedChar.length;
      }
      
      if (currentNormalizedPos >= matchIndex + normalizedSearch.length && originalEnd === -1) {
        originalEnd = i + 1;
        break;
      }
    }
    
    if (originalStart === -1 || originalEnd === -1) {
      const directMatch = text.toLowerCase().indexOf(searchTerm.toLowerCase());
      if (directMatch !== -1) {
        matches.push({
          start: directMatch,
          end: directMatch + searchTerm.length,
        });
        normalizedIndex = matchIndex + 1;
        continue;
      }
    }
    
    if (originalStart !== -1 && originalEnd !== -1 && originalStart < originalEnd) {
      matches.push({ start: originalStart, end: originalEnd });
    }
    
    normalizedIndex = matchIndex + 1;
  }
  
  const lowerText = text.toLowerCase();
  const lowerSearch = searchTerm.toLowerCase();
  let directIndex = 0;
  
  while (true) {
    const directMatch = lowerText.indexOf(lowerSearch, directIndex);
    if (directMatch === -1) break;
    const overlaps = matches.some(m => 
      (directMatch >= m.start && directMatch < m.end) ||
      (m.start >= directMatch && m.start < directMatch + lowerSearch.length)
    );
    
    if (!overlaps) {
      matches.push({
        start: directMatch,
        end: directMatch + lowerSearch.length,
      });
    }
    
    directIndex = directMatch + 1;
  }
  
  matches.sort((a, b) => a.start - b.start);
  
  const merged: Array<{ start: number; end: number }> = [];
  for (const match of matches) {
    if (merged.length === 0) {
      merged.push(match);
    } else {
      const last = merged[merged.length - 1];
      if (match.start <= last.end) {
        last.end = Math.max(last.end, match.end);
      } else {
        merged.push(match);
      }
    }
  }
  
  return merged;
}

export function highlightText(
  text: string,
  searchTerm: string,
  highlightClassName?: string
): React.ReactNode {
  if (!searchTerm || !text) {
    return text;
  }
  
  const matches = findMatches(text, searchTerm);
  
  if (matches.length === 0) {
    return text;
  }
  
  const parts: Array<{ text: string; isHighlight: boolean }> = [];
  let lastIndex = 0;
  
  matches.forEach((match) => {
    if (match.start > lastIndex) {
      parts.push({
        text: text.substring(lastIndex, match.start),
        isHighlight: false,
      });
    }
    
    parts.push({
      text: text.substring(match.start, match.end),
      isHighlight: true,
    });
    
    lastIndex = match.end;
  });
  
  if (lastIndex < text.length) {
    parts.push({
      text: text.substring(lastIndex),
      isHighlight: false,
    });
  }
  
  return (
    <>
      {parts.map((part, index) => {
        if (part.isHighlight) {
          return (
            <mark
              key={index}
              className={highlightClassName}
              style={{
                backgroundColor: "#fbbf24",
                color: "#1f2937",
                padding: "0 2px",
                borderRadius: "2px",
              }}
            >
              {part.text}
            </mark>
          );
        }
        return <React.Fragment key={index}>{part.text}</React.Fragment>;
      })}
    </>
  );
}
